// Modelled on Hedge-Fund-Tracking-System apps/web/src/app/login/page.tsx.
// In dev (VITE_DEV_USER_ID + VITE_DEV_ROLE set, or AUTH off on the server),
// the "Continue" button simply navigates to / — the bot accepts the dev
// headers (or skips auth entirely). In prod this is where you'd kick the
// SSO flow that lands a cookie back.
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';

const AUTH_REDIRECT_URL = import.meta.env.VITE_AUTH_REDIRECT_URL;

export default function Login() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from ?? '/';

  // If we're already authenticated, send the user back where they came from.
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  const handleLogin = () => {
    if (AUTH_REDIRECT_URL) {
      window.location.href = AUTH_REDIRECT_URL;
    } else {
      // Dev / no-SSO fallback: just bounce back. The next request will hit
      // /api/auth/me, succeed (dev override or auth off), and the guard
      // will let us through.
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div>
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-primary text-lg font-bold">aH</span>
          </div>
          <h1 className="text-xl font-bold text-foreground">aHedge</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Sign in to view the dashboard
          </p>
        </div>

        <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
          <p className="text-xs text-muted-foreground">
            {AUTH_REDIRECT_URL
              ? 'You will be redirected to your organization’s SSO.'
              : 'Dev / local mode. The button below returns you to the dashboard; the bot’s /api/auth/me endpoint will accept the dev headers (or skip auth entirely when AUTH_ENABLED=false).'}
          </p>
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Checking session…' : AUTH_REDIRECT_URL ? 'Continue to SSO' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
